import { forwardRef, type ElementType, type ReactNode } from 'react';
import * as React from 'react';

import styles from './Dimension.module.css';

type Props<T extends ElementType> = {
  as?: T;
  level: 'one' | 'two' | 'three' | 'four' | 'none';
};

function getClassName<T extends React.ElementType>(level: Props<T>['level']) {
  switch (level) {
    case 'one': {
      return styles.one;
    }
    case 'two': {
      return styles.two;
    }
    case 'three': {
      return styles.three;
    }
    case 'four': {
      return styles.four;
    }
    case 'none': {
      return '';
    }
  }
}

type DimensionProps<T extends React.ElementType> = Props<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof Props<T>>;

export const Dimension = forwardRef(
  <T extends React.ElementType>(
    props: DimensionProps<T>,
    ref: React.ComponentProps<T>['ref']
  ) => {
    const { as: As = 'div', level, ...rest } = props;
    const { className } = rest;

    return (
      <As
        {...rest}
        ref={ref}
        className={`${getClassName(level)} ${className}`}
      />
    );
  }
) as <T extends React.ElementType>(
  props: Props<T> & Omit<React.ComponentProps<T>, keyof Props<T>>
) => ReactNode;
